import React from 'react';
import { Badge, Box, BoxProps, IconButton, IconButtonProps, Tooltip, TooltipProps } from '@chakra-ui/react';
import { FontAwesomeIcon, FontAwesomeIconProps } from '@fortawesome/react-fontawesome';
import { LinkProps } from 'react-router-dom';

interface IconButtonPropsOptional extends Omit<IconButtonProps, 'aria-label'> {
  'aria-label'?: string;
  to?: LinkProps['to'];
}

interface TooltipPropsOptional extends Omit<TooltipProps, 'children'> {
  children?: React.ReactNode;
}

interface IIconButtonWithTooltip {
  label: string;
  iconProps: FontAwesomeIconProps;
  buttonProps?: IconButtonPropsOptional;
  tooltipProps?: TooltipPropsOptional;
  isDisabled?: boolean;
  counter?: number | string | React.ReactNode;
  onClick?: () => void;
  'data-selector'?: string;
}

const TooltipWrapper = React.forwardRef<HTMLDivElement, BoxProps & { hasCounter: boolean }>(
  ({ children, hasCounter, ...rest }, ref) => {
    if (hasCounter) {
      return (
        <Box ref={ref} position={'relative'} display={'inline-flex'} verticalAlign={'middle'} {...rest}>
          {children}
        </Box>
      );
    }

    return (
      <Box as="span" ref={ref} display={'inline-flex'} verticalAlign={'middle'} {...rest}>
        {children}
      </Box>
    );
  },
);

TooltipWrapper.displayName = 'TooltipWrapper';

export const IconButtonWithTooltip = ({
  label,
  onClick,
  iconProps,
  buttonProps,
  tooltipProps,
  counter,
  isDisabled = false,
  'data-selector': dataSelector = 'edit-sidebar-button',
}: IIconButtonWithTooltip): React.ReactElement => {
  return (
    <Tooltip hasArrow placement="top" label={label} {...tooltipProps}>
      <TooltipWrapper hasCounter={!!counter}>
        <IconButton
          {...buttonProps}
          data-selector={dataSelector}
          isDisabled={isDisabled}
          aria-label={label}
          onClick={onClick}
          icon={<FontAwesomeIcon {...iconProps} />}
        />

        {counter && (
          <Badge
            variant={'vivid-pill'}
            colorScheme={'blue'}
            position={'absolute'}
            top={'-0.25em'}
            right={'-0.25em'}
            pointerEvents={'none'}
          >
            {counter}
          </Badge>
        )}
      </TooltipWrapper>
    </Tooltip>
  );
};
